import * as React from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import type { CustomerDetails, CustomerContacts, CustomerPriceListItem, CustomerRules } from './models/CustomerInterfaces';
import CustomerDetail from './CustomerDetails';
import CustomerContact from './CustomerContacts';
import CustomerPricelist from './CustomerPricing';
import CustomerRule from './CustomerRules';
import { GetQuery, GetSystemCompanyId, PostQuery } from './Shared';

interface validationCheck {
    isValid: boolean;
    detailError: boolean;
    contactError: boolean;
    rulesError: boolean;
}

interface validateDetails {
    isValid: boolean;
    errorList: { [key: string]: string }
}

const initCustomerDetails: CustomerDetails = {
    id: 0,
    name: "",
    internalName: "",
    abn: "",
    acn: "",
    streetName: "",
    streetNameTwo: "",
    suburb: "",
    state: "",
    country: "",
    postCode: "",
    gstRegistered: -1,
    accountTypeId: -1,
    accountLimit: 0,
    accountLimitDunny: 0,
    phone: "",
    invoiceEmail: "",
    statusId: 1,
    lockAccount: false,
    terms: -1,
    statusTypeId: -1,
    statusType: "",
    existingStatusTypeId: -1,
    isLinkedToReckonScaffold: false,
    isLinkedToReckonDunny: false,
    accountId: "",
    accountIdDunny: "",
    createScaffoldAccount: false,
    scaffoldNotUsed: false,
    createDunnyAccount: false,
    dunnyNotUsed: false,
    accountContact: "",
    quoteNotesDunny: "",
    quoteNotes: "",
    priceListTierId: -1,
    calloutFee: 0,
    fencePartPickupFee: 0,
    fuelSurcharge: 0,
    fuelSurchargePumpClean: 0
}

const CustomerAddData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [reckonSwitchedon, setReckonSwitchedOn] = React.useState(false);
    const [systemCompanyId, setSystemCompanyId] = React.useState(2);
    const [step, setStep] = React.useState(1);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [saving, setSaving] = React.useState(false);

    const initCustomerContact: CustomerContacts = {
        id: 0,
        firstName: "",
        lastName: "",
        phone: "",
        mobile: "",
        email: "",
        emailSubscription: true,
        statusId: 1
    };

    const initCustomerRule: CustomerRules = {
        id: 0,
        companyId: 2,
        allowOnGoingHire: false,
        createScaffoldAudit: false,
        requireOverhireNewPo: false,
        invoicePeriod: 0,
        invoicePeriodVoid: 0,
        publicHolidayCharge: true,
        shutdownPeriodType: 1,
        overhireRateDiscountPercentage: 0,
        splitInvoiceByPo: false,
        gateCode: "",
        sendConfirmationEmail: true,
        invoiceToBePrinted: false,
        eventInvoiceOnDelivery: false,
        invoiceQuotedAmount: false,
        epaTransportCertificateCheck: false,
        chargeFuelSurcharge: true,
        fuelSurchargeNewInvoice: false
    };

    const [customerDetails, setCustomerDetails] = React.useState<CustomerDetails>(initCustomerDetails);
    const [customerContacts, setCustomerContacts] = React.useState<CustomerContacts[]>([initCustomerContact]);
    const [customerRule, setCustomerRule] = React.useState<CustomerRules>(initCustomerRule);
    
    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const apiUrl = "CheckCustomerUserPermission";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            var canAdd = result.data.canEdit
            var canCreateQuoteOnly = result.data.canCreateQuoteOnlyCustomer;
            if (canAdd) {
                //check logged into reckon
                checkReckonLogin();
            } else if (canCreateQuoteOnly) {
                setReckonSwitchedOn(true);
                getCustomerData();
            } else {
                //redirect to 403 permission denied
                navigate("/accessdenied");
            }     
        }
    }

    const checkReckonLogin = async () => {
        //enter the page that quickbooks needs to return to, id blank as not used via invoicing
        var page = {
            page: "customeradd",
            id: 0
        };
        //encode to base 64 so can retrieve it later
        var encoded = window.btoa(JSON.stringify(page));

        const apiUrl = "ReckonCustomerAuthoriseCheck?returnUrl=" + encoded;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            //continue if Quick Books user is authorised or if Quick Books is switched off
            setReckonSwitchedOn(result.data.reckonSwitchedOn);
            if (result.data.isAuthorised || !result.data.reckonSwitchedOn) {
                getCustomerData();
            } else {
                navigate(result.data.reckonLoginUrl);
            }
        }
    }


    const getCustomerData = async () => {
        var sysCompId = await GetSystemCompanyId();

        if (sysCompId === 0) {
            toast.error("Error getting data!");
        } else {
            setSystemCompanyId(sysCompId);
            var custDetails: CustomerDetails = {
                id: 0,
                name: "",
                internalName: "",
                abn: "",
                acn: "",
                streetName: "",
                streetNameTwo: "",
                suburb: "",
                state: "",
                country: "",
                postCode: "",
                gstRegistered: -1,
                accountTypeId: -1,
                accountLimit: 0,
                accountLimitDunny: 0,
                phone: "",
                invoiceEmail: "",
                statusId: 1,
                lockAccount: false,
                terms: -1,
                statusTypeId: -1,
                statusType: "",
                existingStatusTypeId: -1,
                isLinkedToReckonScaffold: false,
                isLinkedToReckonDunny: false,
                accountId: "",
                accountIdDunny: "",
                createScaffoldAccount: false,
                scaffoldNotUsed: false,
                createDunnyAccount: false,
                dunnyNotUsed: false,
                accountContact: "",
                quoteNotesDunny: "",
                quoteNotes: "",
                priceListTierId: -1,
                calloutFee: 0,
                fencePartPickupFee: 0,
                fuelSurcharge: 0,
                fuelSurchargePumpClean: 0
            }
            setCustomerDetails(custDetails);

            //set up customer rule
            var custRule: CustomerRules = {
                id: 0,
                companyId: sysCompId,
                allowOnGoingHire: sysCompId === 3 ? true : false,
                createScaffoldAudit: false,
                requireOverhireNewPo: false,
                invoicePeriod: sysCompId === 3 ? 28 : 0,
                invoicePeriodVoid: 0,
                publicHolidayCharge: true,
                shutdownPeriodType: 1,
                overhireRateDiscountPercentage: 0,
                splitInvoiceByPo: false,
                gateCode: "",
                sendConfirmationEmail: sysCompId === 3 ? true : false,
                invoiceToBePrinted: false,
                eventInvoiceOnDelivery: false,
                invoiceQuotedAmount: false,
                epaTransportCertificateCheck: false,
                chargeFuelSurcharge: true,
                fuelSurchargeNewInvoice: false
            };
            setCustomerRule(custRule);
            setLoading(false);
        }
    }

    const updateAccountType = (newAcctTypeId: number) => {
        let splitInvoice = newAcctTypeId === 3;
        setCustomerRule(prevState => ({ ...prevState, splitInvoiceByPo: splitInvoice }));

        setCustomerDetails(prevState => ({ ...prevState, accountTypeId: newAcctTypeId }));
    }

    const updateDetailsString = (fieldName: string, fieldValue: string) => {
        setCustomerDetails(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const updateDetailsNumber = (fieldName: string, fieldValue: number) => {
        setCustomerDetails(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const updateDetailsChecked = (fieldName: string, fieldValue: boolean, unsetFieldName: string) => {
        if (unsetFieldName != "") {
            setCustomerDetails(prevState => ({ ...prevState, [fieldName]: fieldValue, [unsetFieldName]: false }));
        } else {
            setCustomerDetails(prevState => ({ ...prevState, [fieldName]: fieldValue }));
        }
    }

    const updateDetails = (customer: CustomerDetails) => {
        setCustomerDetails(prevState => ({ ...prevState, customer }));
    }

    const updateContacts = (custContacts: CustomerContacts[]) => {
        setCustomerContacts(custContacts);
    }

    const updatePriceListTierId = (fieldName: string, fieldValue: number) => {
        setCustomerDetails(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const updateCustomPricelist = (_custPriceList: CustomerPriceListItem[]) => {
        //not used when creating a new customer
    }

    const updateQuoteNotes = (scaffoldQuoteNotes: string, dunnyQuoteNotes: string) => {
        setCustomerDetails(prevState => ({ ...prevState, quoteNotes: scaffoldQuoteNotes, quoteNotesDunny: dunnyQuoteNotes }));
    }

    const updateRulesString = (fieldName: string, fieldValue: string) => {
        setCustomerRule(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const updateRulesNumber = (fieldName: string, fieldValue: number) => {
        setCustomerRule(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const updateRulesChecked = (fieldName: string, fieldValue: boolean) => {
        setCustomerRule(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const updateErrors = (updatedErrors: { [key: string]: string }) => {
        setErrors(updatedErrors);
    }

    const goPrev = () => {
        if (step > 1) {
            setStep(step - 1);
        }
    }

    const goNext = () => {
        //need to validate each step
        switch (step) {
            case 1:
                var validDetails = validateDetails();
                if (validDetails.isValid) {
                    setStep(step + 1);
                } else {
                    setErrors(validDetails.errorList);
                    toast.error("Please fix the errors before clicking Next!");
                }
                break;
            case 2:
                var contactErrors = validateContacts();
                if (contactErrors === "") {
                    setStep(step + 1);
                } else {
                    let errorList: { [key: string]: string } = {};
                    errorList["contacts"] = contactErrors;
                    setErrors(errorList);
                    toast.error("Please fix the errors before clicking Next!");
                }
                break;
            case 3:
                //nothing to validate on the pricing step
                setStep(step + 1);
                break;
            case 4:
                save();
                break;
            default: 
                break;
        }
    }

    const validateAll = () => {
        let errorList: { [key: string]: string } = {};
        let formIsValid = true;

        //Validate Customer Details
        let detailsError = validateDetails();
        let detailError = detailsError.isValid;
        if (!detailsError.isValid) {
            errorList = detailsError.errorList;
        }

        //Validate Customer Contacts
        let contactError = false;
        var contactsError = validateContacts();
        if (contactsError != "") {
            errorList["contacts"] = contactsError;
            contactError = true;
        }
        
        //Validate Customer Rules
        var custError = false;
        var custRules = customerRule;
        if (custRules.allowOnGoingHire) {
            if (custRules.invoicePeriod < 1) {
                formIsValid = false;
                custError = true;
                errorList["invoicePeriod"] = "Invoice Period must be > 0 days";
            }
        }

        if (custRules.shutdownPeriodType < 0) {
            formIsValid = false;
            custError = true;
            errorList["shutdownPeriodType"] = "Shutdown Period Charge is required";
        }

        setErrors(errorList);
        
        var valid: validationCheck = {
            isValid: formIsValid,
            detailError: detailError,
            contactError: contactError,
            rulesError: custError,
        }

        return valid;
    }

    const validateDetails = () => {
        var error = false;
        let errorList: { [key: string]: string } = {};

        let custDetails = customerDetails;
        if (custDetails.statusTypeId < 0) {
            error = true;
            errorList["statusTypeId"] = "Status Type is required";
        } else if (custDetails.statusTypeId == 1) {
            //quote only customers only require Name, Account Type and Status
            if (!custDetails.name) {
                error = true;
                errorList["name"] = "Name is required";
            }
            if (!custDetails.internalName) {
                error = true;
                errorList["internalName"] = "Reckon Name is required";
            }
            if (custDetails.accountTypeId < 1) {
                error = true;
                errorList["accountTypeId"] = "Account Type is required";
            }
            if (custDetails.statusId < 0) {
                error = true;
                errorList["statusId"] = "Status is required";
            }
        } else {
            //check reckon details
            if (reckonSwitchedon) {
                if (custDetails.accountId === "" && !custDetails.createScaffoldAccount && !custDetails.scaffoldNotUsed) {
                    error = true;
                    errorList["reckonScaffoldCheck"] = "Either select an existing Reckon Scaffold customer, or tick box to Create a new Reckon Scaffold Customer, or tick box to not create a Reckon Scaffold Customer!";
                }
                if (custDetails.accountIdDunny === "" && !custDetails.createDunnyAccount && !custDetails.dunnyNotUsed) {
                    error = true;
                    errorList["reckonDunnyCheck"] = "Either select an existing Reckon Dunny customer, or tick box to Create a new Reckon Dunny Customer, or tick box to not create a Reckon Dunny Customer!";
                }
                if (custDetails.scaffoldNotUsed && custDetails.dunnyNotUsed) {
                    error = true;
                    errorList["reckonScaffoldCheck"] = "You must link to either Scaffold Reckon OR Dunny Reckon!";
                }
            }

            if (!custDetails.name) {
                error = true;
                errorList["name"] = "Name is required";
            }
            if (!custDetails.internalName) {
                error = true;
                errorList["internalName"] = "Reckon Name is required";
            }
            
            if (!custDetails.streetName) {
                error = true;
                errorList["streetName"] = "Address 1 is required";
            }
            if (!custDetails.suburb) {
                error = true;
                errorList["suburb"] = "Suburb is required";
            }
            if (!custDetails.state) {
                error = true;
                errorList["state"] = "State is required";
            }

            if (custDetails.gstRegistered < 0) {
                error = true;
                errorList["gstRegistered"] = "Registered for GST is required";
            } else {
                if (custDetails.gstRegistered == 1 && !custDetails.abn) {
                    error = true;
                    errorList["abn"] = "ABN is required";
                }
            }

            if (custDetails.accountTypeId < 1) {
                error = true;
                errorList["accountTypeId"] = "Account Type is required";
            } else {
                if (custDetails.accountTypeId == 2 || custDetails.accountTypeId == 3) {
                    var linkingToReckonScaffold = custDetails.accountId != "" || custDetails.createScaffoldAccount;
                    if ((systemCompanyId === 2 || custDetails.id === 0) && linkingToReckonScaffold && custDetails.accountLimit < 1) {
                        error = true;
                        errorList["accountLimit"] = "Account Limit is required";
                    }
                    var linkingToReckonDunny = custDetails.accountIdDunny != "" || custDetails.createDunnyAccount;
                    if ((systemCompanyId === 3 || custDetails.id === 0) && linkingToReckonDunny && custDetails.accountLimitDunny < 1) {
                        error = true;
                        errorList["accountLimitDunny"] = "Account Limit is required";
                    }
                }
            }
            if (!custDetails.phone) {
                error = true;
                errorList["phone"] = "Phone is required";
            }
            if (!custDetails.invoiceEmail) {
                error = true;
                errorList["invoiceEmail"] = "Invoice Email is required";
            }
            if (custDetails.terms < 0) {
                error = true;
                errorList["terms"] = "Account Terms are required";
            }
            if (custDetails.statusId < 0) {
                error = true;
                errorList["statusId"] = "Status is required";
            }
        }

        var detailsError: validateDetails = {
            isValid: !error,
            errorList: errorList
        };

        return detailsError;

    }

    const validateContacts = () => {
        var contactsError = "";
        let custContacts = customerContacts;
        var rowNumber = 0;

        var errorString = "Please enter the following values: ";
        var error = false;

        //last row is a blank new row so ignore
        for (var i = 0; i < custContacts.length - 1; i++) {
            rowNumber += 1;
            var newErrorString = "";
            if (!custContacts[i].firstName) {
                newErrorString += " First Name, ";
                error = true;
            }
            if (!custContacts[i].phone && !custContacts[i].mobile) {
                newErrorString += " Phone or Mobile, ";
                error = true;
            }
            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }
        }

        if (error) {
            contactsError = errorString;
        }

        return contactsError;
    }

    const save = () => {
        let valid = validateAll();
        if (valid.isValid) {
            saveCustomer();
        } else {
            toast.error("Please fix errors on the following:");
            if (valid.detailError) {
                toast.error("Details Screen");
            }
            if (valid.contactError) {
                toast.error("Contacts Screen");
            }
            if (valid.rulesError) {
                toast.error("Rules Screen");
            }
        }
    }

    const saveCustomer = async () => {
        //remove blank row from item list
        let custContacts = [...customerContacts];
        custContacts.pop();

        //just add/update any prices that have changed
        var customPricelist: CustomerPriceListItem[] = [];

        var customerToSave = {
            customerDetails: customerDetails,
            customerContacts: custContacts,
            customPriceList: customPricelist,
            customerRule: customerRule
        };

        var jsonItem = JSON.stringify(customerToSave);

        const apiUrl = "SaveCustomer";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errorList = result.data.messages as any[];
                errorList.map(function (error: any) {
                    toast.error(error.content);
                });
                setSaving(false);
            } else {
                if (result.data.quickBooksError) {
                    toast.error("Dash Customer Saved. Reckon Customer Error:" + result.data.quickBooksErrorMessage);
                    navigate("/customersearch");
                } else {
                    toast.success("Customer Saved");
                    navigate("/customersearch");
                }
            }
            setSaving(true);
        }
    }

    var contents;
    if (step === 1) {
        contents = <CustomerDetail id={0} systemCompanyId={systemCompanyId} customerDetails={customerDetails} reckonSwitchedon={reckonSwitchedon} readOnly={false} errors={errors} updateAccountType={updateAccountType} updateDetailsString={updateDetailsString} updateDetailsNumber={updateDetailsNumber} updateDetailsChecked={updateDetailsChecked} updateCustomer={updateDetails} updateErrors={updateErrors} />
    }
    else if (step === 2) {
        contents = <CustomerContact id={0} readOnly={false} accountContactName={customerDetails.accountContact} accountContactPhone={customerDetails.phone} accountContactEmail={customerDetails.invoiceEmail} customerContacts={customerContacts} updateContacts={updateContacts} errors={errors} />
    }
    else if (step === 3) {
        contents = <CustomerPricelist id={0} readOnly={false} priceListTierId={customerDetails.priceListTierId} calloutFee={customerDetails.calloutFee} fencePartPickupFee={customerDetails.fencePartPickupFee} fuelSurcharge={customerDetails.fuelSurcharge} fuelSurchargePumpClean={customerDetails.fuelSurchargePumpClean} customPriceList={[]} hasCustomPriceList={false} scaffoldQuoteNotes={customerDetails.quoteNotes} dunnyQuoteNotes={customerDetails.quoteNotesDunny} errors={errors} updatePriceListTierId={updatePriceListTierId} updateCustom={updateCustomPricelist} updateQuoteNotes={updateQuoteNotes} updateErrors={updateErrors} />
    }
    else if (step === 4) {
        contents = <CustomerRule id={0} readOnly={false} accountTypeId={customerDetails.accountTypeId} customerRule={customerRule} systemCompanyId={systemCompanyId} errors={errors} updateRulesString={updateRulesString} updateRulesNumber={updateRulesNumber} updateRulesChecked={updateRulesChecked} updateErrors={updateErrors } />
    }

    let contentsFinal = loading
        ? <p><em>Loading...</em></p>
        : contents;

    return <div>
        <h1>New Customer</h1>
        {contentsFinal}
        <button className={step === 2 ? "defaultbutton" : "defaultbutton marginLeftLabelLarge"} type="button" onClick={goPrev} disabled={step === 1 || saving}>Previous</button>
        <button className="defaultbutton defaultbutton-next" type="button" onClick={goNext} disabled={saving}>{step === 4 ? "Finish" : "Next"}</button>
        <div className="marginLeftLabelLarge">{saving ? "Saving Customer..." : ""}</div>
    </div>;
}

export default CustomerAddData;